import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import ApplicationForm from './ApplicationForm';
import RegistrationForm from './RegistrationForm';
import NotFound from './NotFound';
import Footer from './Footer';
import AdminNotifications from './AdminNotifications';

// Move facilities OUTSIDE the component
const facilities = [
  {
    label: 'High-Speed WiFi',
    description: 'Blazing fast internet for all your academic needs',
    Icon: () => (
      <div style={{ fontSize: '32px', fontWeight: 'bold' }}>📶</div>
    ),
    gradient: 'linear-gradient(135deg, #5FC0C1 0%, #4AA9A9 100%)',
  },
  {
    label: '24/7 Security',
    description: 'Round-the-clock protection and peace of mind',
    Icon: () => (
      <div style={{ fontSize: '32px', fontWeight: 'bold' }}>🛡️</div>
    ),
    gradient: 'linear-gradient(135deg, #1B3B4D 0%, #0F2633 100%)',
  },
  {
    label: 'Modern Laundry',
    description: 'State-of-the-art washing and drying facilities',
    Icon: () => (
      <div style={{ fontSize: '32px', fontWeight: 'bold' }}>🧺</div>
    ),
    gradient: 'linear-gradient(135deg, #5FC0C1 0%, #4AA9A9 100%)',
  },
  {
    label: 'Study Spaces',
    description: 'Quiet, well-lit areas perfect for focused learning',
    Icon: () => (
      <div style={{ fontSize: '32px', fontWeight: 'bold' }}>📚</div>
    ),
    gradient: 'linear-gradient(135deg, #1B3B4D 0%, #0F2633 100%)',
  },
  {
    label: 'Backup Systems',
    description: 'Uninterrupted power and water supply guaranteed',
    Icon: () => (
      <div style={{ fontSize: '32px', fontWeight: 'bold' }}>⚡</div>
    ),
    gradient: 'linear-gradient(135deg, #5FC0C1 0%, #4AA9A9 100%)',
  },
  {
    label: 'Recreation Hub',
    description: 'Relaxation areas and entertainment facilities',
    Icon: () => (
      <div style={{ fontSize: '32px', fontWeight: 'bold' }}>🎮</div>
    ),
    gradient: 'linear-gradient(135deg, #1B3B4D 0%, #0F2633 100%)',
  },
];

// Example HomePage component
function HomePage() {
  const navigate = useNavigate();
  const [slide, setSlide] = useState(0);
  const [autoSlide, setAutoSlide] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);
  const [showText, setShowText] = useState(false);
  const [openNotifyModal, setOpenNotifyModal] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [notifyMessage, setNotifyMessage] = useState('');

  // Landing animation sequence
  useEffect(() => {
    // First show the background image
    const imageTimer = setTimeout(() => {
      setIsLoaded(true);
    }, 500);

    // Then animate in the text elements
    const textTimer = setTimeout(() => {
      setShowText(true);
    }, 1200);

    return () => {
      clearTimeout(imageTimer);
      clearTimeout(textTimer);
    };
  }, []);

  useEffect(() => {
    if (!autoSlide) return;
    const timer = setTimeout(() => {
      setSlide((prev) => (prev === 0 ? 1 : 0));
    }, 12000); // Even slower transition (12 seconds)
    return () => clearTimeout(timer);
  }, [slide, autoSlide]);

  const handleNotifySubmit = async () => {
    if (!notifyEmail.trim()) {
      setNotifyMessage('Please enter your email address');
      return;
    }
    
    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(notifyEmail)) {
      setNotifyMessage('Please enter a valid email address');
      return;
    }

    setNotifyLoading(true);
    try {
      const response = await fetch('https://server-l9mf8npez-has-projects-fa0c5fad.vercel.app/api/notify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: notifyEmail,
          project: 'riverview-flat'
        })
      });

      const data = await response.json();

      if (response.ok) {
        setNotifyMessage('✅ Thanks! We\'ll notify you when Riverview Flat is ready!');
        setNotifyEmail('');
        setTimeout(() => {
          setOpenNotifyModal(false);
          setNotifyMessage('');
        }, 2000);
      } else {
        // Show specific error messages from backend
        setNotifyMessage(data.error || 'Error subscribing. Please try again.');
      }
    } catch (error) {
      console.error('Error:', error);
      setNotifyMessage('Failed to subscribe. Please check your connection.');
    } finally {
      setNotifyLoading(false);
    }
  };

  return (
    <React.Fragment>
      {/* Header */}
      <Box sx={{ background: 'linear-gradient(135deg, #5FC0C1 0%, #1B3B4D 100%)', color: '#fff', py: 1, px: 0, borderRadius: '0 0 16px 16px', mb: 0 }}>
        <Container maxWidth="lg" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{
              background: 'rgba(255,255,255,0.98)',
              borderRadius: '12px',
              padding: '8px 20px',
              display: 'flex',
              alignItems: 'center',
              boxShadow: '0 4px 16px rgba(27,59,77,0.15)',
              backdropFilter: 'blur(10px)',
              cursor: 'pointer'
            }} onClick={() => navigate('/')}>
              <img 
                src={process.env.PUBLIC_URL + '/Riverview Luxury Apartments-01.png'} 
                alt="Riverview Luxury Apartments" 
                style={{ 
                  height: '80px',
                  width: 'auto',
                  objectFit: 'contain',
                  display: 'block'
                }} 
              />
            </Box>
          </Box>
          <Button 
            variant="contained" 
            size="large" 
            sx={{ 
              fontWeight: 700, 
              borderRadius: 3,
              background: 'rgba(255,255,255,0.95)',
              color: '#1B3B4D',
              border: '2px solid rgba(255,255,255,0.8)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                background: 'rgba(255,255,255,1)',
                color: '#5FC0C1',
                border: '2px solid #5FC0C1',
                boxShadow: '0 6px 25px rgba(95,192,193,0.3)',
                transform: 'translateY(-2px)'
              }
            }} 
            onClick={() => navigate('/apply')}
          >
            Apply for 2026
          </Button>
        </Container>
      </Box>

      {/* Carousel Slides */}
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh', position: 'relative', px: { xs: 0, md: 4 }, bgcolor: '#faf9f7' }}>
        {/* Left Arrow Navigator */}
        <Button
          sx={{
            position: 'absolute',
            left: 16,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 10,
            minWidth: 0,
            width: 48,
            height: 48,
            borderRadius: '50%',
            bgcolor: 'rgba(95,192,193,0.9)',
            color: '#fff',
            boxShadow: 2,
            display: { xs: 'none', md: 'flex' }
          }}
          onClick={() => setSlide(slide === 0 ? 1 : 0)}
          aria-label="Previous Slide"
        >
          {'<'}
        </Button>
        {/* Right Arrow Navigator */}
        <Button
          sx={{
            position: 'absolute',
            right: 16,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 10,
            minWidth: 0,
            width: 48,
            height: 48,
            borderRadius: '50%',
            bgcolor: 'rgba(95,192,193,0.9)',
            color: '#fff',
            boxShadow: 2,
            display: { xs: 'none', md: 'flex' }
          }}
          onClick={() => setSlide(slide === 1 ? 0 : 1)}
          aria-label="Next Slide"
        >
          {'>'}
        </Button>
        <Box sx={{ width: '100%', minHeight: '80vh', position: 'relative' }}>
          <Box
            sx={{
              width: '100%',
              minHeight: '80vh',
              display: slide === 0 ? 'flex' : 'none',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, #f8f4f0 0%, #f1f1f1 100%)',
              transition: 'transform 1.2s cubic-bezier(0.4, 0, 0.2, 1)',
              position: 'relative',
              zIndex: 1,
              overflow: 'visible',
              py: { xs: 4, md: 8 },
              transform: slide === 0 ? 'translateX(0)' : 'translateX(-100vw)',
              borderTopRightRadius: { xs: 32, md: 32 },
              borderTopLeftRadius: { xs: 32, md: 32 }
            }}
          >
            {/* Background image with entrance animation */}
            <img
              src={process.env.PUBLIC_URL + '/frant_page_ back_image.png'}
              alt="Background"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                zIndex: 0,
                opacity: isLoaded ? 0.45 : 0,
                filter: 'brightness(1.1)',
                pointerEvents: 'none',
                borderTopRightRadius: 32,
                borderTopLeftRadius: 32,
                transition: 'opacity 1.5s cubic-bezier(0.4, 0, 0.2, 1), filter 1.5s ease',
                transform: isLoaded ? 'scale(1)' : 'scale(1.05)'
              }}
            />
            {/* Content Container - No Background */}
            <Container maxWidth="lg" sx={{ 
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              justifyContent: 'space-between',
              mt: { xs: 4, md: 8 },
              px: { xs: 2, md: 4 },
              zIndex: 2
            }}>
              <Box sx={{ flex: 2, minWidth: 0, textAlign: { xs: 'center', md: 'left' } }}>
                {/* Main headline - slides in from left */}
                <Typography variant="h3" fontWeight={900} sx={{ 
                  fontSize: { xs: 32, md: 56 },
                  background: 'linear-gradient(135deg, #5FC0C1 0%, #1B3B4D 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                  mb: 2,
                  opacity: showText ? 1 : 0,
                  transform: showText ? 'translateX(0px)' : 'translateX(-80px)',
                  transition: 'all 1.2s cubic-bezier(0.4, 0, 0.2, 1) 0.3s'
                }}>
                  Welcome to Riverview
                </Typography>
                
                {/* Subtitle - slides in from left with delay */}
                <Typography variant="h5" fontWeight={600} sx={{ 
                  mb: 3, 
                  fontSize: { xs: 20, md: 28 }, 
                  letterSpacing: 1,
                  color: '#1B3B4D',
                  opacity: showText ? 1 : 0,
                  transform: showText ? 'translateX(0px)' : 'translateX(-60px)',
                  transition: 'all 1.2s cubic-bezier(0.4, 0, 0.2, 1) 0.6s'
                }}>
                  Premium Student Living Experience
                </Typography>
                
                {/* Description - fades up */}
                <Typography variant="body1" sx={{ 
                  mb: 4, 
                  fontSize: { xs: 18, md: 22 }, 
                  color: '#374151', 
                  maxWidth: 650, 
                  lineHeight: 1.7,
                  opacity: showText ? 1 : 0,
                  transform: showText ? 'translateY(0px)' : 'translateY(40px)',
                  transition: 'all 1.2s cubic-bezier(0.4, 0, 0.2, 1) 0.9s'
                }}>
                  Where comfort meets community. Experience modern amenities, prime locations, and thoughtfully designed spaces that inspire success in your academic journey.
                </Typography>
                
                {/* CTA Button - slides up */}
                <Button 
                  variant="contained" 
                  size="large" 
                  sx={{ 
                    fontWeight: 700, 
                    px: 6, 
                    py: 2,
                    fontSize: { xs: 18, md: 20 },
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #5FC0C1 0%, #4AA9A9 100%)',
                    boxShadow: '0 8px 32px rgba(95, 192, 193, 0.4)',
                    opacity: showText ? 1 : 0,
                    transform: showText ? 'translateY(0px)' : 'translateY(40px)',
                    transition: 'all 1.2s cubic-bezier(0.4, 0, 0.2, 1) 1.2s',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #4AA9A9 0%, #1B3B4D 100%)',
                      boxShadow: '0 12px 40px rgba(95, 192, 193, 0.6)',
                      transform: showText ? 'translateY(-4px)' : 'translateY(40px)',
                    }
                  }} 
                  onClick={() => { setSlide(1); setAutoSlide(false); }}
                >
                  🌟 Explore Riverview
                </Button>
              </Box>
              
              {/* Image - slides in from right */}
              <Box sx={{ 
                flex: 1, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                minWidth: 0, 
                mt: { xs: 4, md: 0 },
                ml: { xs: 0, md: 4 },
                opacity: showText ? 1 : 0,
                transform: showText ? 'translateX(0px) scale(1)' : 'translateX(80px) scale(0.8)',
                transition: 'all 1.4s cubic-bezier(0.4, 0, 0.2, 1) 1.5s'
              }}>
                <Box sx={{
                  bgcolor: '#fff',
                  borderRadius: 6,
                  boxShadow: '0 20px 60px rgba(95,192,193,0.2)',
                  border: '4px solid #fff',
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: { xs: 280, md: 380 },
                  height: { xs: 180, md: 240 },
                  '&:hover': {
                    transform: 'scale(1.05)',
                    boxShadow: '0 25px 80px rgba(217,119,6,0.3)',
                  }
                }}>
                  <img
                    src={process.env.PUBLIC_URL + '/welcoming_page_student.png'}
                    alt="Welcoming Student"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: 4
                    }}
                  />
                </Box>
              </Box>
            </Container>
          </Box>
          <Box
            sx={{
              width: '100%',
              minHeight: '80vh',
              display: slide === 1 ? 'flex' : 'none',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              zIndex: 1,
              transition: 'transform 1.2s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: slide === 1 ? 'translateX(0)' : 'translateX(100vw)',
              background: 'linear-gradient(135deg, #E0F5F5 0%, #B8E5E5 100%)',
              borderTopRightRadius: { xs: 32, md: 32 },
              borderTopLeftRadius: { xs: 32, md: 32 },
              py: { xs: 4, md: 6 },
              px: { xs: 2, md: 4 }
            }}
          >
            {/* Background Pattern */}
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundImage: 'radial-gradient(circle at 25% 25%, rgba(95,192,193,0.15) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(27,59,77,0.1) 0%, transparent 50%)',
              borderTopRightRadius: 32,
              borderTopLeftRadius: 32,
              zIndex: 0
            }} />

            {/* Header Section */}
            <Box sx={{ 
              textAlign: 'center', 
              mb: { xs: 4, md: 6 }, 
              zIndex: 2,
              opacity: slide === 1 ? 1 : 0,
              transform: slide === 1 ? 'translateY(0px)' : 'translateY(-30px)',
              transition: 'all 1s cubic-bezier(0.4, 0, 0.2, 1) 0.3s'
            }}>
              <Typography variant="h3" sx={{ 
                fontWeight: 800, 
                mb: 2,
                background: 'linear-gradient(135deg, #5FC0C1 0%, #1B3B4D 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontSize: { xs: 28, md: 48 }
              }}>
                Premium Facilities & Amenities
              </Typography>
              <Typography variant="h6" sx={{ 
                color: '#0F2633', 
                maxWidth: '700px', 
                mx: 'auto',
                lineHeight: 1.8,
                fontWeight: 500,
                fontSize: { xs: 16, md: 20 }
              }}>
                Experience luxury student living with world-class facilities designed for your success and comfort
              </Typography>
            </Box>

            {/* Facilities Grid */}
            <Container maxWidth="lg" sx={{ zIndex: 2 }}>
              <Grid container spacing={{ xs: 2, md: 3 }}>
                {facilities.map((item) => (
                  <Grid item xs={12} sm={6} md={4} key={item.label}>
                    <Card sx={{
                      height: '100%',
                      borderRadius: 4,
                      border: '1px solid rgba(95,192,193,0.3)',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      background: 'rgba(255,255,255,0.9)',
                      backdropFilter: 'blur(10px)',
                      opacity: slide === 1 ? 1 : 0,
                      transform: slide === 1 ? 'translateY(0px)' : 'translateY(40px)',
                      transitionDelay: `${0.6 + (facilities.indexOf(item) * 0.1)}s`,
                      '&:hover': {
                        transform: slide === 1 ? 'translateY(-12px)' : 'translateY(40px)',
                        boxShadow: '0 20px 40px rgba(217,119,6,0.15)',
                        borderColor: 'rgba(217,119,6,0.3)'
                      }
                    }}>
                      <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Box sx={{
                          width: 64,
                          height: 64,
                          borderRadius: '50%',
                          background: item.gradient,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mx: 'auto',
                          mb: 3,
                          boxShadow: '0 8px 24px rgba(217,119,6,0.3)'
                        }}>
                          <div>📱</div>
                        </Box>
                        <Typography variant="h6" sx={{ 
                          fontWeight: 700, 
                          mb: 2,
                          color: '#0F2633',
                          fontSize: { xs: 18, md: 20 }
                        }}>
                          {item.label}
                        </Typography>
                        <Typography variant="body2" sx={{ 
                          color: '#a8a29e', 
                          lineHeight: 1.6,
                          fontSize: { xs: 14, md: 15 }
                        }}>
                          {item.description}
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* Call to Action */}
              <Box sx={{ 
                textAlign: 'center', 
                mt: { xs: 4, md: 6 },
                opacity: slide === 1 ? 1 : 0,
                transform: slide === 1 ? 'translateY(0px)' : 'translateY(30px)',
                transition: 'all 1s cubic-bezier(0.4, 0, 0.2, 1) 1.2s'
              }}>
                <Button 
                  variant="contained" 
                  size="large" 
                  sx={{ 
                    fontWeight: 700, 
                    px: 6, 
                    py: 2,
                    fontSize: { xs: 16, md: 18 },
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #5FC0C1 0%, #4AA9A9 100%)',
                    boxShadow: '0 8px 32px rgba(95, 192, 193, 0.4)',
                    textTransform: 'none',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #4AA9A9 0%, #1B3B4D 100%)',
                      boxShadow: '0 12px 40px rgba(95, 192, 193, 0.6)',
                      transform: 'translateY(-2px)'
                    }
                  }} 
                  onClick={() => navigate('/apply')}
                >
                  🎓 Start Your Application
                </Button>
                <Typography variant="body2" sx={{ 
                  mt: 2, 
                  color: '#a8a29e',
                  fontSize: 14
                }}>
                  Join hundreds of students who call Riverview home
                </Typography>
              </Box>
            </Container>
          </Box>
          {/* Carousel Dots */}
          <Box sx={{ position: 'absolute', bottom: 32, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 2, zIndex: 10 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: slide === 0 ? '#5FC0C1' : '#ccc', transition: 'bgcolor 0.3s' }} />
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: slide === 1 ? '#5FC0C1' : '#ccc', transition: 'bgcolor 0.3s' }} />
          </Box>
        </Box>
      </Box>

      {/* Gallery Section - Restaurant Inspired */}
      <Box sx={{ py: { xs: 6, md: 10 }, px: { xs: 2, md: 4 }, bgcolor: '#E0F5F5', position: 'relative' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
            <Typography variant="h3" sx={{ 
              fontWeight: 700, 
              mb: 2,
              background: 'linear-gradient(135deg, #5FC0C1 0%, #1B3B4D 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontSize: { xs: 28, md: 42 }
            }}>
              Experience Riverview Luxury Apartments
            </Typography>
            <Typography variant="h6" sx={{ 
              color: '#64748b', 
              maxWidth: '600px', 
              mx: 'auto',
              lineHeight: 1.8,
              fontWeight: 400,
              fontSize: { xs: 16, md: 18 }
            }}>
              Discover our premium accommodation experience through these moments
            </Typography>
          </Box>

          {/* Gallery Grid */}
          <Grid container spacing={{ xs: 2, md: 3 }}>
            <Grid item xs={12} md={6}>
              <Card sx={{ 
                height: { xs: 250, md: 350 }, 
                position: 'relative', 
                overflow: 'hidden',
                borderRadius: 3,
                boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                transition: 'all 0.4s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 30px 60px rgba(0,0,0,0.15)',
                }
              }}>
                <CardMedia
                  component="img"
                  height="100%"
                  image="/welcoming_page_student.png"
                  alt="Student Welcome Experience"
                  sx={{ 
                    objectFit: 'cover',
                    transition: 'transform 0.6s ease',
                    '&:hover': { transform: 'scale(1.05)' }
                  }}
                />
                <Box sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                  color: 'white',
                  p: 3
                }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                    Student Welcome
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    A warm welcome awaits every student
                  </Typography>
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Grid container spacing={{ xs: 2, md: 3 }} sx={{ height: '100%' }}>
                <Grid item xs={12}>
                  <Card sx={{ 
                    height: { xs: 120, md: 165 }, 
                    position: 'relative', 
                    overflow: 'hidden',
                    borderRadius: 3,
                    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                    transition: 'all 0.4s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 25px 50px rgba(0,0,0,0.15)',
                    }
                  }}>
                    <CardMedia
                      component="img"
                      height="100%"
                      image="/bathroom.jpg"
                      alt="Premium Facilities"
                      sx={{ 
                        objectFit: 'cover',
                        transition: 'transform 0.6s ease',
                        '&:hover': { transform: 'scale(1.05)' }
                      }}
                    />
                    <Box sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                      color: 'white',
                      p: 2
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 14 }}>
                        Premium Facilities
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
                <Grid item xs={12}>
                  <Card sx={{ 
                    height: { xs: 120, md: 165 }, 
                    position: 'relative', 
                    overflow: 'hidden',
                    borderRadius: 3,
                    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                    transition: 'all 0.4s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 25px 50px rgba(0,0,0,0.15)',
                    }
                  }}>
                    <CardMedia
                      component="img"
                      height="100%"
                      image="/applaying_person.png"
                      alt="Application Process"
                      sx={{ 
                        objectFit: 'cover',
                        transition: 'transform 0.6s ease',
                        '&:hover': { transform: 'scale(1.05)' }
                      }}
                    />
                    <Box sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                      color: 'white',
                      p: 2
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 14 }}>
                        Easy Application
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Riverview Luxury Apartments Gallery Section */}
      <Box sx={{background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)', padding: {xs: '40px 20px', md: '80px 40px'}, color: 'white', textAlign: 'center'}}>
        <Container maxWidth="lg">
          <Typography variant="h3" sx={{fontWeight: 800, marginBottom: '15px', color: '#fff', fontSize: {xs: '28px', md: '48px'}, background: 'linear-gradient(135deg, #5FC0C1 0%, #4AA9A9 100%)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'}}>
            🏢 Riverview Luxury Apartments
          </Typography>
          <Typography variant="h5" sx={{fontSize: {xs: '16px', md: '20px'}, marginBottom: '50px', color: '#e5e7eb', maxWidth: '600px', mx: 'auto', lineHeight: 1.8}}>
            Premium student accommodation designed for success. Experience modern living with state-of-the-art facilities.
          </Typography>
          
          <Grid container spacing={3} sx={{mb: 4}}>
            {/* Image 1 */}
            <Grid item xs={12} md={6}>
              <Card sx={{borderRadius: 3, overflow: 'hidden', boxShadow: '0 20px 50px rgba(0,0,0,0.4)', transition: 'all 0.4s ease', '&:hover': {transform: 'translateY(-8px)', boxShadow: '0 30px 70px rgba(0,0,0,0.5)'}}}>
                <Box sx={{position: 'relative', paddingBottom: '66.67%', overflow: 'hidden', backgroundColor: '#000'}}>
                  <img 
                    src={process.env.PUBLIC_URL + '/riverview/bedroom-1.jpeg'} 
                    alt="Riverview Bedroom" 
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center',
                      imageRendering: 'crisp-edges',
                      WebkitImageRendering: 'crisp-edges',
                      filter: 'brightness(1.05) contrast(1.1)',
                      transition: 'filter 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.filter = 'brightness(1.15) contrast(1.15)'}
                    onMouseLeave={(e) => e.target.style.filter = 'brightness(1.05) contrast(1.1)'}
                  />
                </Box>
              </Card>
            </Grid>
            
            {/* Image 2 */}
            <Grid item xs={12} md={6}>
              <Card sx={{borderRadius: 3, overflow: 'hidden', boxShadow: '0 20px 50px rgba(0,0,0,0.4)', transition: 'all 0.4s ease', '&:hover': {transform: 'translateY(-8px)', boxShadow: '0 30px 70px rgba(0,0,0,0.5)'}}}>
                <Box sx={{position: 'relative', paddingBottom: '66.67%', overflow: 'hidden', backgroundColor: '#000'}}>
                  <img 
                    src={process.env.PUBLIC_URL + '/riverview/bedroom-2.jpeg'} 
                    alt="Riverview Shared Bedroom" 
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center',
                      imageRendering: 'crisp-edges',
                      WebkitImageRendering: 'crisp-edges',
                      filter: 'brightness(1.05) contrast(1.1)',
                      transition: 'filter 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.filter = 'brightness(1.15) contrast(1.15)'}
                    onMouseLeave={(e) => e.target.style.filter = 'brightness(1.05) contrast(1.1)'}
                  />
                </Box>
              </Card>
            </Grid>

            {/* Image 3 */}
            <Grid item xs={12} md={4}>
              <Card sx={{borderRadius: 3, overflow: 'hidden', boxShadow: '0 20px 50px rgba(0,0,0,0.4)', transition: 'all 0.4s ease', '&:hover': {transform: 'translateY(-8px)', boxShadow: '0 30px 70px rgba(0,0,0,0.5)'}}}>
                <Box sx={{position: 'relative', paddingBottom: '100%', overflow: 'hidden', backgroundColor: '#000'}}>
                  <img 
                    src={process.env.PUBLIC_URL + '/riverview/study-area.jpeg'} 
                    alt="Riverview Study Area" 
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center',
                      imageRendering: 'crisp-edges',
                      WebkitImageRendering: 'crisp-edges',
                      filter: 'brightness(1.05) contrast(1.1)',
                      transition: 'filter 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.filter = 'brightness(1.15) contrast(1.15)'}
                    onMouseLeave={(e) => e.target.style.filter = 'brightness(1.05) contrast(1.1)'}
                  />
                </Box>
              </Card>
            </Grid>

            {/* Image 4 */}
            <Grid item xs={12} md={4}>
              <Card sx={{borderRadius: 3, overflow: 'hidden', boxShadow: '0 20px 50px rgba(0,0,0,0.4)', transition: 'all 0.4s ease', '&:hover': {transform: 'translateY(-8px)', boxShadow: '0 30px 70px rgba(0,0,0,0.5)'}}}>
                <Box sx={{position: 'relative', paddingBottom: '100%', overflow: 'hidden', backgroundColor: '#000'}}>
                  <img 
                    src={process.env.PUBLIC_URL + '/riverview/laundry.jpeg'} 
                    alt="Riverview Laundry Facility" 
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center',
                      imageRendering: 'crisp-edges',
                      WebkitImageRendering: 'crisp-edges',
                      filter: 'brightness(1.05) contrast(1.1)',
                      transition: 'filter 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.filter = 'brightness(1.15) contrast(1.15)'}
                    onMouseLeave={(e) => e.target.style.filter = 'brightness(1.05) contrast(1.1)'}
                  />
                </Box>
              </Card>
            </Grid>

            {/* Image 5 */}
            <Grid item xs={12} md={4}>
              <Card sx={{borderRadius: 3, overflow: 'hidden', boxShadow: '0 20px 50px rgba(0,0,0,0.4)', transition: 'all 0.4s ease', '&:hover': {transform: 'translateY(-8px)', boxShadow: '0 30px 70px rgba(0,0,0,0.5)'}}}>
                <Box sx={{position: 'relative', paddingBottom: '100%', overflow: 'hidden', backgroundColor: '#000'}}>
                  <img 
                    src={process.env.PUBLIC_URL + '/riverview/gym.jpeg'} 
                    alt="Riverview Common Lounge" 
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center',
                      imageRendering: 'crisp-edges',
                      WebkitImageRendering: 'crisp-edges',
                      filter: 'brightness(1.05) contrast(1.1)',
                      transition: 'filter 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.filter = 'brightness(1.15) contrast(1.15)'}
                    onMouseLeave={(e) => e.target.style.filter = 'brightness(1.05) contrast(1.1)'}
                  />
                </Box>
              </Card>
            </Grid>

            {/* Image 6 */}
            <Grid item xs={12} md={4}>
              <Card sx={{borderRadius: 3, overflow: 'hidden', boxShadow: '0 20px 50px rgba(0,0,0,0.4)', transition: 'all 0.4s ease', '&:hover': {transform: 'translateY(-8px)', boxShadow: '0 30px 70px rgba(0,0,0,0.5)'}}}>
                <Box sx={{position: 'relative', paddingBottom: '100%', overflow: 'hidden', backgroundColor: '#000'}}>
                  <img 
                    src={process.env.PUBLIC_URL + '/riverview/dining.jpeg'} 
                    alt="Riverview Dining Area" 
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center',
                      imageRendering: 'crisp-edges',
                      WebkitImageRendering: 'crisp-edges',
                      filter: 'brightness(1.05) contrast(1.1)',
                      transition: 'filter 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.filter = 'brightness(1.15) contrast(1.15)'}
                    onMouseLeave={(e) => e.target.style.filter = 'brightness(1.05) contrast(1.1)'}
                  />
                </Box>
              </Card>
            </Grid>

            {/* Image 7 */}
            <Grid item xs={12} md={4}>
              <Card sx={{borderRadius: 3, overflow: 'hidden', boxShadow: '0 20px 50px rgba(0,0,0,0.4)', transition: 'all 0.4s ease', '&:hover': {transform: 'translateY(-8px)', boxShadow: '0 30px 70px rgba(0,0,0,0.5)'}}}>
                <Box sx={{position: 'relative', paddingBottom: '100%', overflow: 'hidden', backgroundColor: '#000'}}>
                  <img 
                    src={process.env.PUBLIC_URL + '/riverview/gym.jpeg'} 
                    alt="Riverview Fitness Center" 
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center',
                      imageRendering: 'crisp-edges',
                      WebkitImageRendering: 'crisp-edges',
                      filter: 'brightness(1.05) contrast(1.1)',
                      transition: 'filter 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.filter = 'brightness(1.15) contrast(1.15)'}
                    onMouseLeave={(e) => e.target.style.filter = 'brightness(1.05) contrast(1.1)'}
                  />
                </Box>
              </Card>
            </Grid>
          </Grid>

          <Button variant="contained" size="large" onClick={() => setOpenNotifyModal(true)} sx={{fontWeight: 700, px: 6, py: 1.5, fontSize: '16px', borderRadius: 2, background: 'linear-gradient(135deg, #5FC0C1 0%, #4AA9A9 100%)', boxShadow: '0 8px 32px rgba(95, 192, 193, 0.4)', '&:hover': {background: 'linear-gradient(135deg, #4AA9A9 0%, #1B3B4D 100%)', boxShadow: '0 12px 40px rgba(95, 192, 193, 0.6)', transform: 'translateY(-2px)'}}}>
            Notify Me When Ready
          </Button>
        </Container>
      </Box>

      {/* Notify Modal */}
      <Dialog open={openNotifyModal} onClose={() => setOpenNotifyModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{fontWeight: 700, fontSize: '24px', background: 'linear-gradient(135deg, #5FC0C1 0%, #1B3B4D 100%)', color: 'white'}}>
          🚀 Get Notified
        </DialogTitle>
        <DialogContent sx={{pt: 3}}>
          <Typography variant="body1" sx={{mb: 2, color: '#666'}}>
            Enter your email address and we'll notify you as soon as Riverview Flat is ready for bookings!
          </Typography>
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            placeholder="your.email@example.com"
            value={notifyEmail}
            onChange={(e) => {
              setNotifyEmail(e.target.value);
              setNotifyMessage('');
            }}
            disabled={notifyLoading}
            sx={{mb: 2}}
            variant="outlined"
          />
          {notifyMessage && (
            <Alert severity={notifyMessage.includes('✅') ? 'success' : 'error'} sx={{mb: 2}}>
              {notifyMessage}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{p: 2, gap: 1}}>
          <Button onClick={() => setOpenNotifyModal(false)} disabled={notifyLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleNotifySubmit} 
            variant="contained" 
            disabled={notifyLoading}
            sx={{background: 'linear-gradient(135deg, #5FC0C1 0%, #4AA9A9 100%)', '&:hover': {background: 'linear-gradient(135deg, #4AA9A9 0%, #1B3B4D 100%)'}}}
          >
            {notifyLoading ? 'Subscribing...' : 'Subscribe'}
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
    );
}

function App() {
  return (
    <Router>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <Box sx={{ flex: 1 }}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/apply" element={<ApplicationForm />} />
            <Route path="/register" element={<RegistrationForm />} />
            <Route path="/admin/notifications" element={<AdminNotifications />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Box>
        <Footer />
      </Box>
    </Router>
  );
}

export default App;

